import { useEffect, useLayoutEffect, useRef } from 'react';

/**
 * [FLIP-REORDER 2026-06-26] Smoothly animate rows when their ORDER changes — e.g. an
 * Inventory row pinned to the top because it's flagged red drops to its sorted position
 * once a PM is assigned. Without this the row "jumps"; with it the row glides into place.
 *
 * Technique: FLIP (First-Last-Invert-Play). After React reorders the DOM, we measure each
 * row's new top, offset it back to where it WAS (Invert), then animate the offset to 0
 * (Play). We animate ONLY `transform` via the Web Animations API, so it's GPU-composited
 * (no layout thrash) and honours `prefers-reduced-motion` at the platform level.
 *
 * Usage:
 *   const bodyRef = useFlipReorder<HTMLTableSectionElement>([renderedRows]);
 *   <tbody ref={bodyRef}> {rows.map(r => <tr data-flip-key={r.id} .../>)} </tbody>
 *
 * Notes:
 *  - `deps` must change exactly when the row order/content can change (pass the rendered
 *    array) so it never runs on unrelated re-renders (hover, etc.).
 *  - Rows are matched by a STABLE `data-flip-key` (the row id) so reorders are tracked
 *    even though React keys/DOM nodes are reused.
 *  - Newly added / removed rows are NOT slid (no prior position) — they just appear, which
 *    is the correct, non-distracting behaviour.
 *  - Massive reorders (e.g. sorting a multi-thousand-row table) are skipped (instant) so
 *    they never feel janky; single-row moves and normal reorders animate.
 */
const DURATION_MS = 320;
const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)'; // ease-out — decelerates into place (premium feel)
const MAX_ANIMATED_ROWS = 400;                    // beyond this, reorder is too large to animate nicely

/** READ: one forced layout, then every offsetTop read is cheap (no interleaved writes). */
function readTops(container: HTMLElement): Map<string, number> {
  const tops = new Map<string, number>();
  for (const row of container.querySelectorAll<HTMLElement>('[data-flip-key]')) {
    const key = row.getAttribute('data-flip-key');
    if (key != null) tops.set(key, row.offsetTop);
  }
  return tops;
}

/* [TAB-FREEZE 2026-09-23] Run the first snapshot when the browser is idle. requestAnimationFrame
   was not late enough: other effects of the same render (the KPI strip measuring itself, the sticky
   header) dirty the layout again before the next frame, so the read still forced a full layout of
   the table — 190 ms on Inventory. Idle time means the layout is already clean. */
const scheduleIdle = (fn: () => void): number => {
  const ric = (window as any).requestIdleCallback;
  return ric ? ric(fn, { timeout: 1000 }) : window.setTimeout(fn, 300);
};
const cancelIdle = (id: number): void => {
  const cic = (window as any).cancelIdleCallback;
  if (cic) cic(id); else window.clearTimeout(id);
};

export function useFlipReorder<T extends HTMLElement>(deps: ReadonlyArray<unknown>) {
  const ref = useRef<T | null>(null);
  const prevTops = useRef<Map<string, number>>(new Map());
  const firstReadFrame = useRef(0);

  useLayoutEffect(() => {
    const container = ref.current;
    if (!container) return;
    const prev = prevTops.current;

    /* [TAB-FREEZE 2026-09-23] With no earlier positions nothing can animate, so the snapshot is
       taken in idle time instead of inside this layout effect. Reading offsetTop here forces the
       whole table through layout at the exact moment the newly opened tab is being built —
       profiled at 183 ms on Inventory, spent on measurements that could not be used. A reorder
       (prev filled) still measures inline, because FLIP has to invert before the browser paints
       the new order. */
    if (prev.size === 0) {
      cancelIdle(firstReadFrame.current);
      firstReadFrame.current = scheduleIdle(() => {
        const c = ref.current;
        if (c) prevTops.current = readTops(c);
      });
      return;
    }
    cancelIdle(firstReadFrame.current);

    const rows = Array.from(container.querySelectorAll<HTMLElement>('[data-flip-key]'));
    const newTops = readTops(container);
    prevTops.current = newTops; // record for next time regardless of whether we animate now

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    // Which rows actually moved?
    const moves: { row: HTMLElement; dy: number }[] = [];
    for (const row of rows) {
      const key = row.getAttribute('data-flip-key');
      if (key == null) continue;
      const before = prev.get(key);
      const after = newTops.get(key);
      if (before == null || after == null) continue; // added/removed — appear in place
      const dy = before - after;
      if (dy !== 0) moves.push({ row, dy });
    }
    if (moves.length === 0 || moves.length > MAX_ANIMATED_ROWS) return;

    // PLAY: invert to the old position, then glide to 0. Runs inside the layout effect
    // (before paint) so there's no flash at the new position first.
    for (const { row, dy } of moves) {
      row.animate(
        [{ transform: `translateY(${dy}px)` }, { transform: 'translateY(0)' }],
        { duration: DURATION_MS, easing: EASING },
      );
    }
  }, deps);

  useEffect(() => () => cancelIdle(firstReadFrame.current), []);

  return ref;
}
